import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect, lazy, Suspense } from "react";
import { useDispatch } from "react-redux";
import styled from "styled-components";
import { setCredentials, setInitialized } from "./store/authSlice";
import { refreshToken } from "./api/auth";
import { dotSpinner } from "ldrs";
import { trackPageView } from "./utils/analytics";
import { captureAttribution, trackVisit } from "./utils/attribution";
import { useScrollRestoration } from "./hooks/useScrollRestoration";
import MainLayout from "./components/MainLayout";
import ProtectedRoute from "./components/ProtectedRoute";
import AdminLayout from "./components/AdminLayout";

dotSpinner.register();

const PageLoaderWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100vh;
  background: #0a0a0a;
`;

const Landing = lazy(() => import("./pages/Landing"));
const About = lazy(() => import("./pages/About"));
const Gallery = lazy(() => import("./pages/Gallery"));
const Contact = lazy(() => import("./pages/Contact"));
const SizeGuide = lazy(() => import("./pages/SizeGuide"));
const Shop = lazy(() => import("./pages/Shop"));
const Product = lazy(() => import("./pages/Product"));
const Cart = lazy(() => import("./pages/Cart"));
const Checkout = lazy(() => import("./pages/Checkout"));
const Atelier = lazy(() => import("./pages/Atelier"));
const CollectionsList = lazy(() => import("./pages/CollectionsList"));
const CollectionPage = lazy(() => import("./pages/CollectionPage"));
const TrackOrder = lazy(() => import("./pages/TrackOrder"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));
const PaymentFailed = lazy(() => import("./pages/PaymentFailed"));
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const AdminRegister = lazy(() => import("./pages/AdminRegister"));
const AdminForgotPassword = lazy(() => import("./pages/AdminForgotPassword"));
const AdminResetPassword = lazy(() => import("./pages/AdminResetPassword"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminProducts = lazy(() => import("./pages/AdminProducts"));
const AdminOrders = lazy(() => import("./pages/AdminOrders"));
const AdminAnalytics = lazy(() => import("./pages/AdminAnalytics"));
const AdminPromoCodes = lazy(() => import("./pages/AdminPromoCodes"));
const AdminAtelier = lazy(() => import("./pages/AdminAtelier"));
const AdminMarketing = lazy(() => import("./pages/AdminMarketing"));
const AdminSettings = lazy(() => import("./pages/AdminSettings"));
const AdminGallery = lazy(() => import("./pages/AdminGallery"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Legal = lazy(() => import("./pages/Legal"));
const CookieBanner = lazy(() => import("./components/CookieBanner"));
// const CustomShirt = lazy(() => import("./pages/CustomShirt"));

function PageLoader() {
  return (
    <PageLoaderWrap>
      <l-dot-spinner size="40" speed="0.9" color="var(--accent)" />
    </PageLoaderWrap>
  );
}

function ScrollManager() {
  const { pathname } = useLocation();
  useScrollRestoration();
  useEffect(() => {
    trackPageView(pathname);
    // Re-runs per route so a mid-visit arrival from a new source is picked up.
    // trackVisit() no-ops once the session has been counted.
    captureAttribution();
    trackVisit();
  }, [pathname]);
  return null;
}

export default function App() {
  const dispatch = useDispatch();

  useEffect(() => {
    // Safety timeout - if refresh hangs (backend unreachable), force initialize after 4s
    const safety = setTimeout(() => {
      console.warn("[auth] refresh timed out - forcing initialized");
      dispatch(setInitialized());
    }, 4000);

    refreshToken()
      .then((data) => {
        console.log("[auth] refresh success:", data.user?.email);
        dispatch(setCredentials(data));
      })
      .catch((err) => {
        console.log("[auth] refresh failed:", err.message || err);
      })
      .finally(() => {
        clearTimeout(safety);
        dispatch(setInitialized());
      });
  }, [dispatch]);

  return (
    <BrowserRouter>
      <ScrollManager />
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Public store */}
          <Route
            path="/"
            element={
              <MainLayout>
                <Landing />
              </MainLayout>
            }
          />
          <Route
            path="/about"
            element={
              <MainLayout>
                <About />
              </MainLayout>
            }
          />
          <Route
            path="/gallery"
            element={
              <MainLayout>
                <Gallery />
              </MainLayout>
            }
          />
          <Route
            path="/contact"
            element={
              <MainLayout>
                <Contact />
              </MainLayout>
            }
          />
          <Route
            path="/size-guide"
            element={
              <MainLayout>
                <SizeGuide />
              </MainLayout>
            }
          />
          <Route
            path="/shop"
            element={
              <MainLayout>
                <Shop />
              </MainLayout>
            }
          />
          <Route
            path="/product/:id"
            element={
              <MainLayout>
                <Product />
              </MainLayout>
            }
          />
          <Route
            path="/cart"
            element={
              <MainLayout>
                <Cart />
              </MainLayout>
            }
          />
          <Route
            path="/checkout"
            element={
              <MainLayout>
                <Checkout />
              </MainLayout>
            }
          />
          <Route
            path="/atelier"
            element={
              <MainLayout>
                <CollectionsList />
              </MainLayout>
            }
          />
          <Route
            path="/atelier/order"
            element={
              <MainLayout>
                <Atelier />
              </MainLayout>
            }
          />
          <Route
            path="/atelier/:slug"
            element={
              <MainLayout>
                <CollectionPage />
              </MainLayout>
            }
          />
          {/* <Route
            path="/custom-shirt"
            element={
              <MainLayout>
                <CustomShirt />
              </MainLayout>
            }
          /> */}
          <Route
            path="/track-order"
            element={
              <MainLayout>
                <TrackOrder />
              </MainLayout>
            }
          />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/failed" element={<PaymentFailed />} />

          {/* Admin auth */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route
            path="/admin/forgot-password"
            element={<AdminForgotPassword />}
          />
          <Route
            path="/admin/reset-password"
            element={<AdminResetPassword />}
          />
          {process.env.REACT_APP_SHOW_REGISTER === "true" && (
            <Route path="/admin/register" element={<AdminRegister />} />
          )}

          {/* Admin protected */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <AdminDashboard />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/products"
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <AdminProducts />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/orders"
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <AdminOrders />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/analytics"
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <AdminAnalytics />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/promo-codes"
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <AdminPromoCodes />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/atelier"
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <AdminAtelier />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/marketing"
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <AdminMarketing />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/settings"
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <AdminSettings />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/gallery"
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <AdminGallery />
                </AdminLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/terms"
            element={
              <MainLayout>
                <Legal page="terms" />
              </MainLayout>
            }
          />
          <Route
            path="/privacy"
            element={
              <MainLayout>
                <Legal page="privacy" />
              </MainLayout>
            }
          />
          <Route
            path="/refund"
            element={
              <MainLayout>
                <Legal page="refund" />
              </MainLayout>
            }
          />
          <Route
            path="/shipping"
            element={
              <MainLayout>
                <Legal page="shipping" />
              </MainLayout>
            }
          />

          <Route
            path="*"
            element={
              <MainLayout>
                <NotFound />
              </MainLayout>
            }
          />
        </Routes>
        <CookieBanner />
      </Suspense>
    </BrowserRouter>
  );
}
